import { DatePicker, DateTimePicker, TimePicker } from '@mui/x-date-pickers';
import classnames from 'classnames';
import { utcToZonedTime, zonedTimeToUtc } from 'date-fns-tz';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';

import {
  DateTimeType,
  dateStringToDate,
  defaultDateFormat,
  defaultDateTimeFormat,
  defaultTimeFormat,
  defaultTimeZone,
} from '@utils/dateTimeFormatter';

import styles from './DateTimeRange.module.scss';

export type DateFilterValue = {
  from: Date | null;
  to: Date | null;
};

type DateTimeRangeProps = {
  value: DateFilterValue;
  onChange: (value: DateFilterValue) => void;
  type?: DateTimeType;
  testId?: string;
  name?: string;
  disableFuture?: boolean;
};

const languageNamespace = 'date-time-range';

function DateTimeRange({
  value,
  onChange,
  type = 'date',
  testId,
  name,
  disableFuture,
}: DateTimeRangeProps) {
  const { t } = useTranslation(languageNamespace);

  const convertTime = (date: Date | null, toUKTime = true) => {
    if (!date) {
      return null;
    }
    return toUKTime ? utcToZonedTime(date, defaultTimeZone) : zonedTimeToUtc(date, defaultTimeZone);
  };

  const { from, to } = useMemo(() => {
    return value && Object.keys(value).length
      ? { from: convertTime(value?.from), to: convertTime(value?.to) }
      : { from: null, to: null };
  }, [value]);

  const handleChange = (field: 'from' | 'to', date: Date | null) => {
    const newValue = { from: value?.from, to: value?.to };
    newValue[field] = date ? convertTime(date, false) : null;
    onChange?.(newValue);
  };

  const fieldNamePrefix = useMemo(() => name ?? uuidv4(), [name]);
  const fromFieldName = `${fieldNamePrefix}-from`;
  const toFieldName = `${fieldNamePrefix}-to`;

  const maxDate = useMemo(() => {
    if (disableFuture) {
      const date = new Date();
      date.setHours(23, 59, 59);
      return date;
    }
  }, [disableFuture]);

  const handleBlur = (
    field: 'from' | 'to',
    dateStringFormat: string,
  ): React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement> => {
    return (e) => {
      const date = dateStringToDate(e.target.value, dateStringFormat);
      handleChange(field, date);
    };
  };

  if (type === 'date') {
    return (
      <div data-testid={testId} className={classnames(styles.container, styles.dateOnly)}>
        <DatePicker
          slotProps={{
            textField: {
              placeholder: t('from'),
              name: fromFieldName,
              inputProps: { 'aria-label': fromFieldName },
              onBlur: handleBlur('from', defaultDateFormat),
            },
          }}
          format={defaultDateFormat}
          value={from}
          onAccept={(value) => handleChange('from', value)}
          maxDate={maxDate}
        />
        <DatePicker
          slotProps={{
            textField: {
              placeholder: t('to'),
              name: toFieldName,
              onBlur: handleBlur('to', defaultDateFormat),
              inputProps: { 'aria-label': toFieldName },
            },
          }}
          format={defaultDateFormat}
          value={to}
          onAccept={(value) => handleChange('to', value)}
          minDate={from}
          maxDate={maxDate}
        />
      </div>
    );
  }

  if (type === 'time') {
    return (
      <div data-testid={testId} className={styles.container}>
        <TimePicker
          slotProps={{
            textField: {
              placeholder: t('from'),
              name: fromFieldName,
              inputProps: { 'aria-label': fromFieldName },
              onBlur: handleBlur('from', defaultTimeFormat),
            },
          }}
          format={defaultTimeFormat}
          value={from}
          onAccept={(value) => handleChange('from', value)}
          maxTime={maxDate}
          ampm={false}
        />
        <TimePicker
          slotProps={{
            textField: {
              placeholder: t('to'),
              name: toFieldName,
              inputProps: { 'aria-label': toFieldName },
              onBlur: handleBlur('to', defaultTimeFormat),
            },
          }}
          format={defaultTimeFormat}
          value={to}
          onAccept={(value) => handleChange('to', value)}
          minTime={from}
          maxTime={maxDate}
          ampm={false}
        />
      </div>
    );
  }

  if (type === 'datetime') {
    return (
      <div data-testid={testId} className={styles.container}>
        <DateTimePicker
          slotProps={{
            textField: {
              placeholder: t('from'),
              name: fromFieldName,
              inputProps: { 'aria-label': fromFieldName },
              onBlur: handleBlur('from', defaultDateTimeFormat),
            },
          }}
          format={defaultDateTimeFormat}
          value={from}
          // Using onAccept here so the value only updates when the "OK" button is clicked
          onAccept={(value) => handleChange('from', value)}
          maxDate={maxDate}
          ampm={false}
        />
        <DateTimePicker
          slotProps={{
            textField: {
              placeholder: t('to'),
              name: toFieldName,
              inputProps: { 'aria-label': toFieldName },
              onBlur: handleBlur('to', defaultDateTimeFormat),
            },
          }}
          format={defaultDateTimeFormat}
          value={to}
          onAccept={(value) => handleChange('to', value)}
          minDateTime={from}
          maxDate={maxDate}
          ampm={false}
        />
      </div>
    );
  }
}

export { DateTimeRange };
